import { useCallback, useMemo } from 'react'

import { Link, Mail } from '@mui/icons-material'
import { Avatar, Typography } from '@mui/material'
import { OrganizationRoleRamInvitation, OrganizationRoleRamInvitationRolesEnum } from '@nativewaves/nw-cloud-sdk/org'
import { ColumnDef } from '@tanstack/react-table'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { RoleSelector } from 'components/RoleSelector'
import { ResolvedUser } from 'domains/Organizations/Organization/AccessSettings/Invitations'
import { useOrganizationInvitationUpdateMutation } from 'hooks/mutations/org'
import { invitationsOverviewRoute } from 'pages/NWPlatform/Organizations/Organization/Members'

export const useColumns = () => {
  const { t } = useTranslation(['common', 'domain'])

  const { organizationId } = invitationsOverviewRoute.useParams()

  const { mutate: updateInvitation } = useOrganizationInvitationUpdateMutation()

  const handleUpdate = useCallback(
    ({ invitationId, roles }: { invitationId: string; roles: OrganizationRoleRamInvitationRolesEnum[] }) => {
      updateInvitation({
        invitationId,
        organizationId,
        organizationRoleRamInvitationEdit: {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          roles,
        },
      })
    },
    [organizationId, updateInvitation],
  )

  return useMemo<ColumnDef<OrganizationRoleRamInvitation>[]>(
    () => [
      {
        accessorKey: 'recipientType',
        header: () => null,
        meta: {
          sizing: {
            width: 64,
            px: 2,
          },
          align: 'center',
        },
        cell: ({ getValue }) => {
          const recipientType = getValue<OrganizationRoleRamInvitation['recipientType']>()

          return (
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'background.levels.900' }}>
              {recipientType === 'Email' && <Mail fontSize="inherit" color="info" />}
              {recipientType === 'None' && <Link fontSize="inherit" color="info" />}
            </Avatar>
          )
        },
      },
      {
        accessorKey: 'recipientEmail',
        header: 'Invited User',
        meta: {
          sizing: {
            minWidth: 300,
          },
        },
        cell: ({ getValue, row }) => {
          const email = getValue<string | undefined>()
          const { recipientType } = row.original

          return (
            <Typography
              sx={{ color: (theme) => (!getValue() ? theme.palette.action.disabled : 'inherit') }}
              variant="inherit"
              noWrap
            >
              {recipientType === 'None' ? 'Link' : email}
            </Typography>
          )
        },
      },
      {
        accessorKey: 'changedByUserId',
        header: 'Created by',
        meta: {
          sizing: {
            minWidth: 120,
            maxWidth: 180,
          },
        },
        cell: ({ getValue }) => <ResolvedUser userId={`user-${getValue<string>()}`} />,
      },
      {
        accessorKey: 'expirationTime',
        header: t('expiresIn'),
        meta: {
          sizing: {
            minWidth: 120,
            maxWidth: 300,
          },
        },
        cell: ({ getValue }) => (
          <Typography
            sx={{ color: (theme) => (!getValue() ? theme.palette.action.disabled : 'inherit') }}
            variant="inherit"
            noWrap
          >
            {formatDistanceToNow(parseISO(getValue<string>()), { addSuffix: false })}
          </Typography>
        ),
      },
      {
        accessorKey: 'roles',
        header: () => null,
        meta: {
          align: 'end',
          sizing: {
            minWidth: 280,
            maxWidth: 320,
            px: 6,
          },
        },
        cell: ({ getValue, row }) => (
          <RoleSelector
            baseRoles={Object.values(OrganizationRoleRamInvitationRolesEnum)}
            roles={[...getValue<Set<OrganizationRoleRamInvitationRolesEnum>>()]}
            onUpdate={({ roles }) => handleUpdate({ invitationId: row.original.id, roles })}
          />
        ),
      },
    ],
    [handleUpdate, t],
  )
}
